<template lang="pug">
	.audio-tracker-wapi-plugin(v-if="plugin")
		.flex-col.full-width.space-large.center.align-center
			.flex-row.justify.flex-1.align-center
				.left.flex-1
					DxCheckBox(:value.sync="enabled", :text="$tCaption('Active')")
				.font-big.flex-1.upper.grey.text-center {{pluginItem.name()}}
				.flex-row.justify.flex-1.align-center
					.left
					.flex-row.justify-end
						PresetSelector.flex-1(:type="pluginItem.id", @select-preset="onSelectPreset", :presetData="value", :storedPreset="config && config.selectedPreset")
			.flex-col.space.full-width(:class="{ 'disabled-plugin': !enabled }")
				.flex-row.space-medium
					.flex-col.space.sidechain-panel(v-if="supportSidechain", ref="sidechainPanel")
						.nowrap.upper.font-mini.grey {{$tCaption('Sidechain tracks')}}
						DxScrollView(v-if="sidechainPanelHeight", :width="100", :height="sidechainPanelHeight", scrollVisible="always")
							.flex-col.space
								DxCheckBox.nowrap.font-mini(v-for="aggTrack in availableSidechainTracks", :text="getShortenedName(aggTrack.name)", :hint="aggTrack.name", :value="sidechainTrackNumbers.includes(aggTrack.trackNumber)", 
									@value-changed="e => $Data.toggleSelectedIdInArray(aggTrack.trackNumber, sidechainTrackNumbers)", :key="aggTrack.trackNumber")
					.flex-1.full-width
						template(v-if="plugin.component")
							component(ref="cmp", :is="plugin.component()", :config="config", :tracker="tracker", :source="source", :playing="playing", :instance="plugin", @change="v => value = v")
						template(v-else-if="pluginUI", v-for="row in pluginUI.rows", :row="row")
							WAPIPluginRow(:row="row", @value-change="onElemValueChange", :value="value")
</template>

<script>
	import _ from 'lodash';
	import VueTypes from 'vue-types';
	import DxButton from 'devextreme-vue/button';
	import DxCheckBox from 'devextreme-vue/check-box';
	import Loader from 'app-core/Loader.vue';	
	import PresetSelector from '../AudioTrackerPresetSelector.vue';
	import SliderButton from '../AudioTrackerSliderButton.vue';
	import RoundButton from '../AudioTrackerRoundButton.vue';
	import AudioTrackerVUMeter from '../AudioTrackerVUMeter.vue';
	import pluginsList from './plugins-list';
	import WAPIPluginRow from './WAPIPluginRow.vue';
	import DxScrollView from 'devextreme-vue/scroll-view';

	export default {
		translationCategory: 'audioTracker.AudioTracker',
		name: 'audio-tracker-wapi-plugin',
		components: { DxButton, Loader, PresetSelector, SliderButton, RoundButton, AudioTrackerVUMeter, DxCheckBox, WAPIPluginRow, DxScrollView },
		props: {
			plugins: VueTypes.any.def(null),
			source: VueTypes.any.def(null),
			audioContext: VueTypes.any.def(null),
			config: VueTypes.any.def(null),
			pluginId: VueTypes.string.def('limiter'),
			playing: VueTypes.bool.def(false),
			tracker: VueTypes.any.def(null),
			aggregatedTrack: VueTypes.any.def(null)
		},
		model: {
			prop: 'config',
			event: 'change'
		},
		data() {
			var pluginItem = pluginsList.getPluginItem(this.pluginId);
			pluginsList.getPlugin(this.pluginId).then(r => {
				this.plugin = r.instance;
				this.pluginUI = this.plugin.getUI();
				this.defaultValues = _.cloneDeep(this.plugin?.getDefaultValues ? this.plugin.getDefaultValues() : this.plugin.getValues());
				this.initValues = this.config?.data && _.cloneDeep(this.config?.data) || this.defaultValues;
				this.initConfig = _.cloneDeep(this.config);
				this.plugin.setValues(this.initValues);
				this.value = _.cloneDeep(this.initValues);
			});

			return {
				isPlaying: this.playing,
				initedSource: null,
				value: null,
				initValues: null,
				initConfig: null,
				pluginItem,
				plugin: null,
				defaultValues: null,
				enabled: this.config?.enabled != null ? this.config?.enabled : true,
				playInitialized: false,
				sidechainTrackNumbers: [...(this.config?.sidechainTrackNumbers || [])],
				sidechainPanelHeight: 0
			};
		},
		computed: {
			hasChanges() {
				return this.plugin && JSON.stringify(this.defaultValues || {}) !== JSON.stringify(this.plugin.getValues() || {});
			},
			supportSidechain() {
				return !!this.pluginItem?.supportSidechain;
			},
			availableSidechainTracks() {
				return this.tracker.aggregatedTracks.filter(f => f != this.aggregatedTrack);
			}
		},
		watch: {
			value: {
				handler() {
					this.emitConfigChanged();
				},
				deep: true
			},
			enabled() {
				this.emitConfigChanged();
				this.setToAggTrack(() => {
					this.refreshPlay();
				});
			},
			isPlaying(v) {
				this.$emit('playing', v);
				if (this.enabled) {
					this.plugin.setPlayState(v ? 1 : 0);
				}
			},
			playing(v) {
				if (!v) {
					this.deactivatePlugin();
				} else {
					if (!this.isPlaying) {
						setTimeout(() => {
							this.initPlugin();
						}, 100);
					}
				}
				this.isPlaying = v;
			}
		},
		mounted() {
			if (__DEBUG__)	
				window.wapiPlugin = this;
			this.$setToAggTrack = _.debounce(this.setToAggTrack, 100, {
				'leading': false,
				'trailing': true
			});
			this.$eventsBus.$on('audio-tracker-stop', () => {
				if (__DEBUG__)
					console.debug('Stop');
				if (this._isDestroyed) return;
				this.isPlaying = false;
			});
			this.$eventsBus.$on('audio-tracker-play', () => {
				if (__DEBUG__)
					console.debug('Play');
				if (this._isDestroyed) return;
				this.isPlaying = true;
			});

			this.$setToAggTrack();

			if (this.isPlaying) {
				setTimeout(() => {
					this.initPlugin();
				}, 100);
			}

			setImmediate(() => {
				this.sidechainPanelHeight = (this.$refs.sidechainPanel?.offsetHeight - 40) || 100;
			});
		},
		destroyed() {
			delete this.$setToAggTrack;
			this.deactivatePlugin();

			this.$eventsBus.$off('audio-tracker-stop');
			this.$eventsBus.$off('audio-tracker-play'); 
		},
		methods: {
			getShortenedName(str, limit) {
				limit = limit || 14;
				return (str || '').slice(0, limit) + ((str || '').length > limit ? '..' : '');
			},
			emitConfigChanged() {
				this.$emit('change', { instanceId: this.$Data.uuid(), ...this.config, enabled: this.enabled, data: this.value, sidechainTrackNumbers: this.sidechainTrackNumbers });
			},
			onElemValueChange(elem, v) {
				if (__DEBUG__)
					console.debug('Set value onElemValueChange', { elem, v });
				elem.setValue(v);
				this.value = _.cloneDeep(this.plugin.getValues());
			},
			setValue() {
				this.value = _.cloneDeep(this.plugin.getValues());
				this.emitConfigChanged();
			},
			onSelectPreset(data, preset) {
				if (__DEBUG__)
					console.debug('Preset select', { data, preset });
				this.plugin.setValues(_.cloneDeep(data));
				this.value = _.cloneDeep(this.plugin.getValues());
				this.enabled = true;
				this.$set(this.config, 'selectedPreset', _.pick(preset, ['id', 'name', 'data']))
			},
			save() {
				this.setToAggTrack();
				this.$emit('save');
			},
			setToAggTrack(callback, config) {
				var aggTrackPlugin = this.plugins.find(f => f.instanceId && f.instanceId === this.config?.instanceId);
				if (!aggTrackPlugin)
					aggTrackPlugin = this.plugins.find(f => f.id === this.pluginId);

				if (config) {
					if (!aggTrackPlugin) {
						return;
					}
					Object.keys(aggTrackPlugin).map(f => delete aggTrackPlugin[f]);
					_.assign(aggTrackPlugin, config);

					if (!aggTrackPlugin.id) {
						this.plugins.splice(this.plugins.findIndex(f => f === aggTrackPlugin), 1);
					}
				}

				if (!config) {
					if (!aggTrackPlugin) {
						aggTrackPlugin = { instanceId: this.$Data.uuid(), id: this.pluginId, data: null, enabled: this.enabled };
						this.plugins.push(aggTrackPlugin);
					}
					if (!aggTrackPlugin.instanceId)
						aggTrackPlugin.instanceId = this.$Data.uuid();

					aggTrackPlugin.data = _.clone(this.value);
					aggTrackPlugin.enabled = this.enabled;
					aggTrackPlugin.hasChanges = this.hasChanges;
					this.$set(aggTrackPlugin, 'sidechainTrackNumbers', this.sidechainTrackNumbers);
					this.$set(aggTrackPlugin, 'selectedPreset', this.config?.selectedPreset);
				}

				setTimeout(() => {
					if (callback)
						callback();
				}, 100);
				
			},
			cancel() {
				if (__DEBUG__)
					console.debug('Set value cancel');
				this.value = _.clone(this.initValues);
				this.enabled = !!this.initConfig?.enabled;
				this.setToAggTrack(void 0, this.initConfig);
				this.$emit('cancel');
			},
			async clearValues() {
				if (__DEBUG__)
					console.debug('Set value clearValues');
				this.value = _.cloneDeep(this.defaultValues);
				await this.plugin.setValues(_.cloneDeep(this.value));
				this.$forceUpdate();
			},
			togglePlay() {
				if (this.isPlaying) {
					this.stop();
				} else {
					this.play();
				}
			},
			async refreshPlay() {
				if (this.tracker.isPlaying)
					await this.tracker.play(this.tracker.currentTime);
				this.initPlugin();
			},
			stop() {
				this.isPlaying = false;
				if (this.initedSource && this.initedSource.source)
					try {
						this.tracker.stop();
					} catch {
						//If source wasn't initialized
					}
			},
			async play() {
				this.setToAggTrack();
				await this.tracker.play(this.tracker.lastTime);
				this.$nextTick(async () => {
					await this.initPlugin();
					if (this.initedSource.source) {
						setImmediate(() => {
							if (this._isDestroyed) return;
							this.isPlaying = true;
						});
					}
				});
			},
			async initPlugin() {
				let newSource = await this.source();
				if (newSource?.source !== this.initedSource?.source) {
					this.deactivatePlugin();
					this.initedSource = newSource;
					if (this.initedSource.source) {
						var plugin = this.initedSource.effects.filters.wapiNodes.filter(f => f.id === this.pluginItem.id)[0]?.instance;
						if (plugin) {
							await plugin.setValues(this.value);
							this.plugin = plugin;
							this.pluginUI = plugin.getUI();
							this.plugin.setPlayState(this.isPlaying ? 1 : 0);

							if (this.plugin.activateUI) {
								this.plugin.activateUI();
							}
						}
					}
				}
			},
			deactivatePlugin() {
				this.$Data.safe(() => {
					if (this.plugin)
						this.plugin.setPlayState(0);
				});
			}
		}
	};
</script>
<style lang='scss'>
	.audio-tracker-wapi-plugin {
		@import "main/themes/generated/variables.base.scss";

		user-select: none;

		.disabled-plugin {
			opacity: 0.4;
			pointer-events: none;
		}

		.sidechain-panel {
			padding: 8px;
			background: rgba(0,0,0,0.05);
			border-radius: 4px;
			width: 116px;
		}

		.switch {
			border: var(--border-size, 2px) solid #333;
			border-radius: 4px;
			padding: 2px 8px;
			user-select: none;
			cursor: pointer;
			color: #333;
			font-size: var(--font-size, 8px);

			&.active {
				border: var(--border-size, 2px) solid var(--color);
				color: var(--color);
			}
		}
	}
</style>
